import {
  ApiError,
  apiResponseErrorHandler,
} from "@shared/apis/sidekick/api-error-handler";
import { getAuthApiClient } from "@shared/apis/sidekick/api-factories";
import { ChallengeNewPasswordRequiredResponse } from "@shared/apis/sidekick/generated";
import { appEventEmitter } from "@shared/events/app-event-emitter";
import { useMutation } from "@tanstack/react-query";
import { toast } from "@shared/services/toast";

type Props = {
  onSuccess: (data: ChallengeNewPasswordRequiredResponse) => void;
  onError?: (error: ApiError) => void;
};

type Params = {
  newPassword: string;
  session: string;
  email: string;
};

export const useChallengeNewPassword = (props: Props) => {
  const { onSuccess, onError } = props;
  const api = getAuthApiClient();

  const mutation = useMutation({
    mutationFn: async (params: Params) => {
      const response = await api.authControllerChallengeResetPasswordRequired({
        challengeNewPasswordRequiredRequest: {
          session: params.session,
          newPassword: params.newPassword,
          email: params.email,
        },
      });

      onSuccess(response);
      appEventEmitter.emit({ eventType: "sidekick-auth-login" });
    },
    onError: async (e) => {
      const error = await apiResponseErrorHandler(e);
      console.error(
        `Failed to resolve auth challenge to change password`,
        error,
      );
      onError?.(error);
      if (error.status === 429) {
        toast.error(
          "Sorry, we are experiencing a high volume of requests. Please try again later.",
        );
        return;
      }
      if (error.status === 401) {
        toast.error(
          "You are not authorized to perform this action. Your session may have been expired. Please try signing in again.",
        );
        return;
      }
      toast.error(error.errors[0]);
    },
  });

  return mutation;
};
